import { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormikProvider, useFormik } from 'formik';
import { useConfirm } from 'material-ui-confirm';
import * as Yup from 'yup';

import DialogHeader from 'components/DialogHeader';
import { useAppDispatch, useAppSelector } from 'store';
import { TiposMensagemEnum } from 'store/reducers/mensagens';
import {
  setTemplateCategory,
  TemplateInterface,
  templatesSelector,
} from 'store/reducers/templates';
import { MessageVariables, messageVariablesSchema } from 'utils/validators';

import { TemplateSelection } from './components/TemplateSelection';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: 1,
    minHeight: 0,
  },
  paper: {
    '& .MuiDialogTitle-root .MuiTypography-h6': {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightMedium as number,
    },
    '& .MuiDialogContent-root .MuiDialogContentText-root': {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2),
  },
}));

interface ModalEnviarMensagemTemplateProps {
  onSendTemplate: (template: TemplateInterface) => void;
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  whatsappTemplateMessage: TemplateInterface | null;
  messageVariables: MessageVariables;
}

const initialMessageVariables: MessageVariables = {
  header: { url: '', storage_id: '' },
  headerVariables: [],
  headerFile: null,
  body: { variables: [] },
};

export default function ModalEnviarMensagemTemplate({
  onSendTemplate,
  open,
  onClose,
}: ModalEnviarMensagemTemplateProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const form = useFormik<FormValues>({
    initialValues: {
      whatsappTemplateMessage: null,
      messageVariables: {
        header: {
          url: '',
          storage_id: '',
        },
        headerVariables: [],
        headerFile: null,
        body: {
          variables: [],
        },
      },
    },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      whatsappTemplateMessage: Yup.object().required('Selecione um template'),
      messageVariables: messageVariablesSchema,
    }),
    onSubmit: values => {
      if (!values.whatsappTemplateMessage) return;

      const template = { ...values.whatsappTemplateMessage };

      if (template.formFields) {
        template.formFields = {
          ...template.formFields,
          header: template.formFields.header
            ? {
                ...template.formFields.header,
                url: values.messageVariables.header?.url || '',
                storage_id: values.messageVariables.header?.storage_id || '',
                variables: values.messageVariables.headerVariables || [],
              }
            : undefined,
          body: {
            ...template.formFields.body,
            variables: values.messageVariables.body?.variables || [],
          },
        };
      }

      onSendTemplate(template);
      handleClose();
    },
  });

  const { values, handleSubmit, setTouched, setValues, resetForm } = form;
  const { whatsappTemplateMessage: selectedTemplate } = values;

  const { templates } = useAppSelector(templatesSelector);
  const { mensagensAtendimento } = useAppSelector(state => state.mensagens);

  const confirm = useConfirm();

  const [searchTerm, setSearchTerm] = useState('');

  const lastSentTemplateId = useMemo(() => {
    const lastMessage = mensagensAtendimento.find(
      msg => msg.tipo !== TiposMensagemEnum.EVENTO,
    );

    if (
      lastMessage?.tipo === TiposMensagemEnum.TEMPLATE_WHATSAPP &&
      lastMessage?.whatsappTemplateData?.id
    ) {
      return lastMessage.whatsappTemplateData.id;
    }

    return null;
  }, [mensagensAtendimento]);

  const handleTemplateSelect = (template: TemplateInterface) => {
    const { formFields } = template;

    const messageVariables: MessageVariables = {
      header: {
        type: formFields?.header?.type,
        url: '',
        storage_id: '',
      },
      headerVariables:
        formFields?.header?.variables?.map(v => ({
          name: v.name,
          value: '',
        })) || [],
      headerFile: null,
      body: {
        variables:
          formFields?.body?.variables?.map(v => ({
            name: v.name,
            value: '',
          })) || [],
      },
    };

    resetForm({
      values: {
        whatsappTemplateMessage: template,
        messageVariables,
      },
    });
  };

  // Se o filtro foi alterado e o template selecionado saiu da lista atual,
  // limpa a seleção automaticamente.
  useEffect(() => {
    if (
      selectedTemplate &&
      !templates.some(template => template.id === selectedTemplate.id)
    ) {
      resetForm({
        values: {
          whatsappTemplateMessage: null,
          messageVariables: initialMessageVariables,
        },
      });
    }
  }, [selectedTemplate, templates, resetForm]);

  const handleClose = () => {
    setValues(prev => ({
      ...prev,
      whatsappTemplateMessage: null,
      messageVariables: initialMessageVariables,
    }));
    setSearchTerm('');
    onClose();
  };

  const handleSendTemplateMessage = async () => {
    if (!selectedTemplate) return;

    const errors = await form.validateForm();

    if (Object.keys(errors).length > 0) {
      // Força a exibição de todos os erros relevantes
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const touchedVariables: any = {};

      if (values.messageVariables?.header) {
        touchedVariables.header = {
          url: true,
        };
        touchedVariables.headerFile = true;
        touchedVariables.headerVariables =
          values.messageVariables.headerVariables?.map(() => ({
            value: true,
          })) || [];
      }

      if (values.messageVariables?.body) {
        touchedVariables.body = {
          variables:
            values.messageVariables.body.variables?.map(() => ({
              value: true,
            })) || [],
        };
      }

      setTouched({
        whatsappTemplateMessage: true,
        messageVariables: touchedVariables,
      });
      return;
    }

    const isLastSentTemplate = lastSentTemplateId === selectedTemplate.id;

    if (isLastSentTemplate) {
      try {
        await confirm({
          title: 'Este template foi enviado na última mensagem ao contato.',
          description: (
            <>
              Envios repetidos sem resposta podem reduzir a taxa de entrega pela
              <b> Meta</b>. Deseja prosseguir?
            </>
          ),
          confirmationText: 'Enviar',
          cancellationText: 'Cancelar',
          confirmationButtonProps: { color: 'primary', variant: 'contained' },
          dialogProps: { maxWidth: 'xs', classes: { paper: classes.paper } },
        });

        handleSubmit();
      } catch {
        // cancelado
      }

      return;
    }

    handleSubmit();
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <FormikProvider value={form}>
      <Dialog
        open={open}
        onClose={handleClose}
        BackdropProps={{
          onExited: () => {
            dispatch(setTemplateCategory(null));
            form.resetForm();
          },
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { height: 'auto', maxHeight: '90vh', minHeight: 600 },
        }}
      >
        <DialogHeader
          title="Templates"
          onClose={handleClose}
          description="Escolha um modelo de mensagem para enviar."
        />

        <DialogContent className={classes.dialogContent}>
          <TemplateSelection
            open={open}
            searchTerm={searchTerm}
            onChangeSearchTerm={setSearchTerm}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={handleTemplateSelect}
          />
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCancel} color="default">
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSendTemplateMessage}
            disabled={!selectedTemplate}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </FormikProvider>
  );
}
